// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EducationalTooltip } from '@/components/educational-tooltip';
import { PageContent } from '@/components/page-content';
import { SegmentedControl } from '@/components/segmented-control';
import {
  AddCircleOutlineRounded,
  InfoOutlined,
  NorthEastRounded,
  ReplayRounded,
} from '@/constants/hardfin-standard-icons';
import {
  PROJECT_START_CLASSIFICATIONS,
  PROJECT_START_CLASSIFICATION_LABELS,
  ProjectStartClassification,
  projectStartClassificationSchema,
} from '@/constants/project-start-classification';
import {
  Sort,
  SortDirection,
  invert,
  sortDirectionSchema,
} from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useResizeObserverRef } from '@/hooks/use-resize-observer-ref';
import {
  ProjectSortProperty,
  projectSortPropertySchema,
} from '@/project/api/get-projects';
import { ProjectStatusChip } from '@/project/components/project-status-chip';
import {
  ProjectTable,
  ProjectTableColumn,
} from '@/project/components/project-table';
import { useProjects } from '@/project/hooks/use-projects';
import { ProjectRequestedAssetCountChart } from '@/report/components/project-requested-asset-count-chart';
import { ProjectTotalContractValueChart } from '@/report/components/project-total-contract-value-chart';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportCard } from '@/report/components/report-card';
import { ReportLocationMap } from '@/report/components/report-location-map';
import { SparseLocation } from '@/sparse/api/types';
import { getToday } from '@/utilities/date-time';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  colors,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { ReactNode, useRef, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Upcoming project starts report';
const CHART_HEIGHT = 300;
const PROJECT_START_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
];

const PROJECT_START_CLASSIFICATION_ICONS: Record<
  ProjectStartClassification,
  ReactNode
> = {
  renewal: <ReplayRounded fontSize="small" />,
  expansion: <NorthEastRounded fontSize="small" />,
  'new-business': <AddCircleOutlineRounded fontSize="small" />,
};

const PROJECT_START_CLASSIFICATION_OPTIONS = PROJECT_START_CLASSIFICATIONS.map(
  (classification) => ({
    value: classification,
    label: PROJECT_START_CLASSIFICATION_LABELS[classification],
    icon: PROJECT_START_CLASSIFICATION_ICONS[classification],
  })
);

const TABLE_COLUMNS: ProjectTableColumn[] = [
  'start-date',
  'name',
  'workflow-stage',
  'assets',
  'locations',
  'customer',
];

const searchSchema = z.object({
  days: z.number().optional().catch(undefined),
  sort: projectSortPropertySchema.optional().catch(undefined),
  direction: sortDirectionSchema.optional().catch(undefined),
  classifications: z
    .array(projectStartClassificationSchema)
    .optional()
    .catch(['renewal']),
});

export const Route = createFileRoute('/_app/reports/upcoming-project-starts')({
  component: UpcomingProjectStartsReportRoute,
  validateSearch: searchSchema,
});

function UpcomingProjectStartsReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const totalContractValueObserver = useResizeObserverRef<HTMLDivElement>();
  const assetCountObserver = useResizeObserverRef<HTMLDivElement>();

  const [focusId, setFocusId] = useState<string | null>(null);
  const [focusColor, setFocusColor] = useState<string | null>(null);

  const classificationsTooltip = useDisclosure();
  const classificationsIconRef = useRef<SVGSVGElement>(null);

  const dayCount = search.days ?? 90;
  const startBy = getToday().plus({ days: dayCount });
  const startClassifications = search.classifications ?? ['renewal'];
  const sort: Sort<ProjectSortProperty> = {
    property: search.sort ?? 'start',
    direction: search.direction ?? 'asc',
  };

  const { data, isPending } = useProjects({
    sort,
    filter: {
      startClassifications,
      statuses: ['upcoming'],
      startBy: startBy.toISODate(),
      pageSize: 10000, // Reports need all matching projects
    },
  });

  const projects = data ?? [];
  const locations = projects.flatMap(({ locations }) => locations);
  const focusedProject = projects.find((project) => project.id === focusId);
  const emptyState = `No projects found starting in ${pluralize(dayCount, { singular: 'day' })} (before ${startBy.plus({ day: 1 }).toLocaleString({ dateStyle: 'medium' })})`;
  const locationsEmptyState =
    projects.length > 0 &&
    projects.every((project) => project.locations.length === 0)
      ? 'No location data for the upcoming projects'
      : emptyState;

  const handleHoverTotalContractValue = (
    id: string | null,
    color: string | null
  ) => {
    setFocusId(id);
    setFocusColor(color);
  };

  const handleHoverAssetCount = (id: string | null, color: string | null) => {
    setFocusId(id);
    setFocusColor(color);
  };

  const handleChangeDayCount = (count: number) => {
    navigate({ search: { ...search, days: count } });
  };

  const handleChangeStartClassifications = (
    classifications: ProjectStartClassification[]
  ) => {
    if (classifications.length > 0) {
      navigate({ search: { ...search, classifications } });
    }
  };

  const setSort = (property: ProjectSortProperty, direction: SortDirection) => {
    navigate({ search: { ...search, direction, sort: property } });
  };

  const handleSort = (property: ProjectSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invert(sort.direction));
    }

    setSort(property, 'asc');
  };

  const getLocationColor = (location: SparseLocation) => {
    const isFocused = focusedProject?.locations.some(
      ({ id }) => id === location.id
    );

    if (isFocused && focusColor !== null) {
      return focusColor;
    }

    return colors.blueGrey[300];
  };

  return (
    <PageContent>
      <ReportBreadcrumbs title={REPORT_TITLE} />
      <Typography variant="h5">{REPORT_TITLE}</Typography>
      <Typography variant="body2" color="text.secondary">
        Includes all upcoming projects with a start date in the given range
      </Typography>
      <Stack
        spacing={2}
        marginTop={3}
        direction="row"
        justifyContent="space-between"
      >
        <SegmentedControl
          label="Projects starting"
          value={dayCount}
          onChange={handleChangeDayCount}
          options={PROJECT_START_OPTIONS}
        />
        <Stack spacing={1} direction="row" alignItems="center">
          <EducationalTooltip
            title="Filters for upcoming project starts"
            anchorElement={classificationsIconRef.current}
            isOpen={classificationsTooltip.isOpen}
            onClose={classificationsTooltip.close}
          >
            <Box
              rowGap={2}
              columnGap={1}
              display="grid"
              alignItems="start"
              gridTemplateColumns="min-content 1fr"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <ReplayRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  Renewal
                </Typography>
              </Stack>
              <Typography variant="body2">
                The project refers back to a previous project
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <NorthEastRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  Expansion
                </Typography>
              </Stack>
              <Typography variant="body2">
                The project has no previous project, but this customer has a{' '}
                <ProjectStatusChip status="PAST" /> or{' '}
                <ProjectStatusChip status="ACTIVE" /> project
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddCircleOutlineRounded fontSize="inherit" />
                <Typography variant="subtitle2" whiteSpace="nowrap">
                  New business
                </Typography>
              </Stack>
              <Typography variant="body2">
                The project has no previous project, and this customer has no{' '}
                <ProjectStatusChip status="PAST" /> or{' '}
                <ProjectStatusChip status="ACTIVE" /> projects
              </Typography>
            </Box>
          </EducationalTooltip>
          <InfoOutlined
            ref={classificationsIconRef}
            color="action"
            fontSize="small"
            onClick={classificationsTooltip.open}
            sx={{ cursor: 'help' }}
          />
          <SegmentedControl
            multiple
            label="Include"
            value={startClassifications}
            onChange={handleChangeStartClassifications}
            options={PROJECT_START_CLASSIFICATION_OPTIONS}
          />
        </Stack>
      </Stack>
      <Box
        gap={3}
        marginY={3}
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
      >
        <ReportCard ref={totalContractValueObserver.ref} title="Project value">
          {isPending ? (
            <Box
              height={CHART_HEIGHT}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          ) : (
            <ProjectTotalContractValueChart
              groupCount={5}
              width={totalContractValueObserver.width}
              height={CHART_HEIGHT}
              onHover={handleHoverTotalContractValue}
              projects={projects}
              emptyState={emptyState}
            />
          )}
        </ReportCard>
        <ReportCard ref={assetCountObserver.ref} title="Asset count">
          {isPending ? (
            <Box
              height={CHART_HEIGHT}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          ) : (
            <ProjectRequestedAssetCountChart
              groupCount={5}
              width={assetCountObserver.width}
              height={CHART_HEIGHT}
              onHover={handleHoverAssetCount}
              projects={projects}
              emptyState={emptyState}
            />
          )}
        </ReportCard>
        <ReportCard title="Locations">
          <ReportLocationMap
            locations={locations}
            emptyState={locationsEmptyState}
            getColor={getLocationColor}
          />
        </ReportCard>
      </Box>
      <ProjectTable
        sort={sort}
        projects={projects}
        columns={TABLE_COLUMNS}
        onSort={handleSort}
        emptyState={emptyState}
      />
    </PageContent>
  );
}
